/**
 * MJML blocks and the host document they compile inside — Block 0.4 of the
 * marketing plan (plans/execution-plan-2026-09.md §2.2), on the decisions
 * recorded in the backlog under E1 §Authoring: MJML.
 *
 * Two things live here, both pure, both used by the editor's lint and by the
 * API's approval and assembly, so that what the approver saw is what was
 * sent:
 *
 *   - `checkBlockSource` — is this text a block? One or more complete
 *     sections (M2), built from the closed component allowlist (M5), with
 *     nothing but the inline allowlist inside `mj-text` (P9). The browser
 *     build of MJML validates structure at `strict` and will refuse a bare
 *     `mj-text` or an unregistered tag — but it compiles `mj-raw` without
 *     comment, and passes the content of `mj-text`, `mj-button` and
 *     `mj-table` through unescaped: an MSO conditional comment, a
 *     `javascript:` href and an `onerror` attribute all reach the output.
 *     Verified against mjml-browser 5.4.0, not assumed. This is the only
 *     line of defence for content, exactly as the SDR editor's schema is.
 *
 *   - `hostDocument` — the `<mjml>` document a block is compiled inside. A
 *     block cannot be compiled or previewed alone: MJML's grammar is rooted,
 *     and a fragment without `<mjml>` is refused as malformed. The head is
 *     the theme (M6: `mj-attributes`, `mj-font`, `mj-breakpoint` and nothing
 *     else) plus the campaign's preheader as `mj-preview` (M7, P8); the body
 *     is the blocks, verbatim, in order. Deterministic for a given input,
 *     because the assembly cache key and "reconstructable byte-for-byte"
 *     both rest on it.
 */

import { MERGE_TOKEN_PATTERN } from '../tokens/index';
import { tokenize, type TagToken, type Token } from './tokenize';

export interface BlockProblem {
  /** 1-based line in the source; 0 when the problem is the whole thing. */
  line: number;
  message: string;
}

/**
 * M5 — the closed component allowlist. `mj-social-element` is listed because
 * `mj-social` is nothing without it, not as a widening. Everything else in
 * MJML is refused by name below or as unknown.
 */
export const MJML_ALLOWLIST = [
  'mj-section',
  'mj-wrapper',
  'mj-group',
  'mj-column',
  'mj-hero',
  'mj-text',
  'mj-button',
  'mj-image',
  'mj-divider',
  'mj-spacer',
  'mj-table',
  'mj-social',
  'mj-social-element',
] as const;

/** M2 — what may sit at the top of a block. */
export const BLOCK_ROOTS = ['mj-section', 'mj-wrapper', 'mj-hero'] as const;

/**
 * P9 — the inline allowlist for the HTML inside `mj-text`, `mj-button` and
 * `mj-social-element`. Nothing else, no attributes beyond `href`.
 */
export const INLINE_ALLOWLIST = [
  'p',
  'br',
  'strong',
  'em',
  'a',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
] as const;

/**
 * `mj-table` takes "plain HTML" — rows and cells — and passes it through
 * exactly as `mj-text` does, so it gets the same inline rules plus the table
 * elements, with no attributes: a `td` that needs a width or a colour is a
 * layout, and layouts are columns. Decided here rather than per block
 * (E1.19); widen once, if a marketer demonstrably needs it.
 */
export const TABLE_ALLOWLIST = ['table', 'thead', 'tbody', 'tr', 'th', 'td'] as const;

const FORBIDDEN: Record<string, string> = {
  'mj-raw':
    'mj-raw is forbidden. Raw children skip the Outlook ghost tables, the column arithmetic and the media queries — the reasons to use MJML at all (M1).',
  'mj-include':
    'mj-include is ignored by the browser build and disabled by default in MJML 5; a block using it would render in one place and not the other (M5).',
  'mj-html-attributes':
    'mj-html-attributes sets arbitrary attribute names — alt, title, aria-label — which are prose a recipient perceives (M5).',
  'mj-style':
    'mj-style is emitted without HTML escaping, so its content can close the style element and open a new one. Presentation belongs to the theme (M5, M6).',
  mjml: 'A block is one or more sections, not a document (M2). Leave out <mjml>, <mj-head> and <mj-body>.',
  'mj-body': 'A block is one or more sections, not a document (M2). Leave out <mjml>, <mj-head> and <mj-body>.',
  'mj-head': 'A block is one or more sections, not a document (M2). Leave out <mjml>, <mj-head> and <mj-body>.',
  'mj-attributes': 'mj-attributes belongs to the theme: attributes, fonts and the breakpoint are the theme, not the block (M6).',
  'mj-font': 'mj-font belongs to the theme: attributes, fonts and the breakpoint are the theme, not the block (M6).',
  'mj-breakpoint': 'mj-breakpoint belongs to the theme: attributes, fonts and the breakpoint are the theme, not the block (M6).',
  'mj-class': 'mj-class belongs to the theme: attributes, fonts and the breakpoint are the theme, not the block (M6).',
  'mj-title':
    'mj-title reaches <title> and the aria-label a screen reader announces — it is prose, and it belongs to nobody but the campaign (M6).',
  'mj-preview': 'mj-preview belongs to the campaign: the preheader lives there, and is required there (M7).',
};

const ROOT = '#root';

/** Where each allowed element may sit — the same rules MJML enforces at strict, stated up front with a line number. */
const PARENTS: Record<(typeof MJML_ALLOWLIST)[number], readonly string[]> = {
  'mj-section': [ROOT, 'mj-wrapper'],
  'mj-wrapper': [ROOT],
  'mj-hero': [ROOT, 'mj-wrapper'],
  'mj-group': ['mj-section'],
  'mj-column': ['mj-section', 'mj-group'],
  'mj-text': ['mj-column', 'mj-hero'],
  'mj-button': ['mj-column', 'mj-hero'],
  'mj-image': ['mj-column', 'mj-hero'],
  'mj-divider': ['mj-column', 'mj-hero'],
  'mj-spacer': ['mj-column', 'mj-hero'],
  'mj-table': ['mj-column', 'mj-hero'],
  'mj-social': ['mj-column', 'mj-hero'],
  'mj-social-element': ['mj-social'],
};

/** Elements whose children are HTML, checked by the inline rules, not MJML. */
const HTML_CONTENT = new Set(['mj-text', 'mj-button', 'mj-table', 'mj-social-element']);

/** Attributes that carry a URL, on any element: `https:` or a merge token, nothing else. */
const URL_ATTRS = new Set(['href', 'src', 'background-url']);

const WHOLE_TOKEN = new RegExp(`^${MERGE_TOKEN_PATTERN.source}$`, 'i');

function isMergeToken(value: string): boolean {
  return WHOLE_TOKEN.test(value.trim());
}

/**
 * A URL an email may carry: `https:` only — `http:` is a mixed-content
 * warning in some clients and `javascript:`, `data:` and `vbscript:` are the
 * hole — or a merge token minted at send, such as `{{unsubscribe_url}}` or
 * `{{booking_link}}`. Relative paths are refused because an email has no
 * origin to resolve them against.
 */
export function isAcceptableUrl(value: string): boolean {
  return /^https:\/\/\S+$/i.test(value.trim()) || isMergeToken(value);
}

function urlProblem(tag: TagToken, name: string, value: string): string {
  return `<${tag.name} ${name}="${value}"> — a link or image source must be https: or a merge token such as {{booking_link}}.`;
}

export interface BlockCheck {
  ok: boolean;
  problems: BlockProblem[];
}

/**
 * Is this text a block? Every problem carries a line number for the editor's
 * gutter. Approval calls this and then compiles at `strict` (M4); this is
 * the half the compiler does not do.
 */
export function checkBlockSource(source: string): BlockCheck {
  const problems: BlockProblem[] = [];
  const tokens = tokenize(source);
  const stack: TagToken[] = [];
  let roots = 0;

  const parentName = (): string => stack[stack.length - 1]?.name ?? ROOT;

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i]!;

    if (token.kind === 'bad') {
      problems.push({ line: token.line, message: token.message });
      continue;
    }

    if (token.kind === 'comment') {
      problems.push({
        line: token.line,
        message:
          'Comments are not allowed in a block. An MSO conditional comment is the route to hand-written VML, which the allowlist exists to close (M5, P9).',
      });
      continue;
    }

    if (token.kind === 'text') {
      if (token.text.trim() !== '') {
        problems.push({
          line: token.line,
          message: `Text must sit inside mj-text or mj-button; "${token.text.trim().slice(0, 40)}" is between elements and the compiler drops it.`,
        });
      }
      continue;
    }

    // A tag. A close tag of something refused on its open — HTML at column
    // level, a forbidden element — is not a second problem.
    if (token.kind === 'close') {
      if (!(token.name in PARENTS)) continue;
      const open = stack.pop();
      if (!open) {
        problems.push({ line: token.line, message: `</${token.name}> closes nothing.` });
      } else if (open.name !== token.name) {
        problems.push({
          line: token.line,
          message: `</${token.name}> closes <${open.name}> from line ${open.line}.`,
        });
        stack.push(open);
      }
      continue;
    }

    if (!token.name.startsWith('mj-') && token.name !== 'mjml') {
      problems.push({
        line: token.line,
        message: `<${token.name}> is HTML, and HTML is only allowed inside mj-text, mj-button, mj-table and mj-social-element. A table at column level would need mj-raw, which is forbidden (M1).`,
      });
      i = skipContent(tokens, i, token);
      continue;
    }

    const reason = FORBIDDEN[token.name];
    if (reason) {
      problems.push({ line: token.line, message: reason });
      i = skipContent(tokens, i, token);
      continue;
    }

    const name = token.name as (typeof MJML_ALLOWLIST)[number];
    const parents = PARENTS[name];
    if (!parents) {
      problems.push({
        line: token.line,
        message: `<${token.name}> is not on the component allowlist (M5): ${MJML_ALLOWLIST.join(', ')}.`,
      });
      i = skipContent(tokens, i, token);
      continue;
    }

    const parent = parentName();
    if (!parents.includes(parent)) {
      problems.push({
        line: token.line,
        message:
          parent === ROOT
            ? `<${name}> cannot sit at the top of a block. A block is one or more mj-section, mj-wrapper or mj-hero (M2); this needs a ${parents.join(' or ')} around it.`
            : `<${name}> cannot be used inside <${parent}>, only inside: ${parents.filter((p) => p !== ROOT).join(', ')}.`,
      });
    }
    if (parent === ROOT) roots++;

    for (const [attr, value] of Object.entries(token.attrs)) {
      if (attr.startsWith('on')) {
        problems.push({
          line: token.line,
          message: `<${name} ${attr}> — event handler attributes are not allowed.`,
        });
      } else if (URL_ATTRS.has(attr) && !isAcceptableUrl(value)) {
        problems.push({ line: token.line, message: urlProblem(token, attr, value) });
      }
    }

    if (HTML_CONTENT.has(name) && token.kind === 'open') {
      // Consume the content up to this element's close, checking it as HTML.
      const end = findClose(tokens, i + 1, name);
      const inner = tokens.slice(i + 1, end === -1 ? tokens.length : end);
      problems.push(...checkInline(inner, name));
      if (end === -1) {
        problems.push({ line: token.line, message: `<${name}> is never closed.` });
        i = tokens.length;
      } else {
        i = end;
      }
      continue;
    }

    if (token.kind === 'open') stack.push(token);
  }

  for (const open of stack) {
    problems.push({ line: open.line, message: `<${open.name}> is never closed.` });
  }

  if (problems.length === 0 && roots === 0) {
    problems.push({
      line: 0,
      message: 'A block is one or more mj-section, mj-wrapper or mj-hero (M2); this has none.',
    });
  }

  problems.sort((a, b) => a.line - b.line);
  return { ok: problems.length === 0, problems };
}

/**
 * After refusing an element, step past its content: what sits inside a
 * refused element is not a second problem, and reporting it would bury the
 * one that matters under a cascade.
 */
function skipContent(tokens: Token[], i: number, token: TagToken): number {
  if (token.kind !== 'open') return i;
  const end = findClose(tokens, i + 1, token.name);
  return end === -1 ? i : end;
}

/** The index of the close tag for `name` opened just before `from`, or -1. */
function findClose(tokens: Token[], from: number, name: string): number {
  let depth = 0;
  for (let k = from; k < tokens.length; k++) {
    const t = tokens[k]!;
    if (t.kind === 'open' && t.name === name) depth++;
    if (t.kind === 'close' && t.name === name) {
      if (depth === 0) return k;
      depth--;
    }
  }
  return -1;
}

const VOID = new Set(['br']);

/**
 * P9 as a check over the tokens inside one content element. Tags from the
 * inline allowlist (plus the table elements inside `mj-table`), `href` on
 * `a` and no other attribute anywhere, no comments, no `<` that is not a
 * tag. The hand-rolled VML button MJML's own maintainer recommends `mj-text`
 * for fails on its first character.
 */
export function checkInline(tokens: Token[], within: string): BlockProblem[] {
  const problems: BlockProblem[] = [];
  const allowed = new Set<string>([
    ...INLINE_ALLOWLIST,
    ...(within === 'mj-table' ? TABLE_ALLOWLIST : []),
  ]);
  const stack: TagToken[] = [];

  for (const token of tokens) {
    if (token.kind === 'text') continue;
    if (token.kind === 'bad') {
      problems.push({ line: token.line, message: token.message });
      continue;
    }
    if (token.kind === 'comment') {
      problems.push({
        line: token.line,
        message: `A comment inside <${within}> is not allowed: an MSO conditional comment is hand-written VML by another name (P9).`,
      });
      continue;
    }

    if (token.name.startsWith('mj-')) {
      problems.push({
        line: token.line,
        message: `<${token.name}> cannot sit inside <${within}>; MJML elements do not nest in content.`,
      });
      continue;
    }

    if (!allowed.has(token.name)) {
      // Its close tag is not a second problem.
      if (token.kind !== 'close') {
        problems.push({
          line: token.line,
          message: `<${token.name}> is not allowed inside <${within}>. Allowed: ${[...allowed].join(', ')} (P9).`,
        });
      }
      continue;
    }

    if (token.kind === 'close') {
      if (VOID.has(token.name)) {
        problems.push({ line: token.line, message: `</${token.name}> — <${token.name}> is never closed.` });
        continue;
      }
      const open = stack.pop();
      if (!open) {
        problems.push({ line: token.line, message: `</${token.name}> closes nothing.` });
      } else if (open.name !== token.name) {
        problems.push({
          line: token.line,
          message: `</${token.name}> closes <${open.name}> from line ${open.line}.`,
        });
        stack.push(open);
      }
      continue;
    }

    for (const [attr, value] of Object.entries(token.attrs)) {
      if (token.name === 'a' && attr === 'href') {
        if (!isAcceptableUrl(value)) {
          problems.push({ line: token.line, message: urlProblem(token, attr, value) });
        }
      } else {
        problems.push({
          line: token.line,
          message: `<${token.name} ${attr}> — no attributes are allowed inside <${within}> except href on a (P9). Styling is the theme's.`,
        });
      }
    }
    if (token.name === 'a' && !('href' in token.attrs)) {
      problems.push({ line: token.line, message: '<a> without an href is not a link.' });
    }

    if (token.kind === 'open' && !VOID.has(token.name)) stack.push(token);
  }

  for (const open of stack) {
    problems.push({ line: open.line, message: `<${open.name}> inside <${within}> is never closed.` });
  }
  return problems;
}

// ---- The theme and the host document ---------------------------------------

/**
 * `email_themes.tokens` — M6. Keys are `mj-attributes` targets (`mj-all`,
 * `mj-body`, or an allowed component), each a map of attribute → value;
 * plus `mj-font`, a list of `{name, href}`; plus `mj-breakpoint`, a width.
 * Nothing else, because four of `mj-head`'s eight children can carry text a
 * recipient perceives, and the whole point of a theme is that it cannot.
 */
export type ThemeTokens = Record<string, unknown>;

export const THEME_TARGETS = ['mj-all', 'mj-body', ...MJML_ALLOWLIST] as const;

const ATTR = /^[a-z][a-z0-9-]*$/;

export function checkThemeTokens(tokens: unknown): BlockProblem[] {
  const problems: BlockProblem[] = [];
  const at = (message: string) => problems.push({ line: 0, message });
  if (!tokens || typeof tokens !== 'object' || Array.isArray(tokens)) {
    at('A theme is an object of mj-attributes targets, mj-font and mj-breakpoint.');
    return problems;
  }
  for (const [key, value] of Object.entries(tokens as Record<string, unknown>)) {
    if (key === 'mj-font') {
      if (!Array.isArray(value)) {
        at('mj-font must be a list of {name, href}.');
        continue;
      }
      for (const font of value as unknown[]) {
        const f = font as { name?: unknown; href?: unknown };
        if (!f || typeof f.name !== 'string' || !ATTR_VALUE.test(f.name)) {
          at('Every mj-font needs a plain-text name.');
        } else if (typeof f.href !== 'string' || !/^https:\/\/\S+$/.test(f.href)) {
          at(`mj-font "${f.name}" needs an https: href.`);
        }
      }
      continue;
    }
    if (key === 'mj-breakpoint') {
      if (typeof value !== 'string' || !/^\d{2,4}px$/.test(value)) {
        at('mj-breakpoint must be a pixel width such as "480px".');
      }
      continue;
    }
    if (!(THEME_TARGETS as readonly string[]).includes(key)) {
      at(
        `"${key}" is not a theme key. A theme holds mj-attributes targets (${THEME_TARGETS.join(', ')}), mj-font and mj-breakpoint — nothing that can carry text (M6).`,
      );
      continue;
    }
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      at(`${key} must be a map of attribute → value.`);
      continue;
    }
    for (const [attr, raw] of Object.entries(value as Record<string, unknown>)) {
      if (!ATTR.test(attr)) {
        at(`${key}: "${attr}" is not an attribute name.`);
      } else if (typeof raw !== 'string' || !ATTR_VALUE.test(raw)) {
        at(
          `${key} ${attr}: a theme value is presentation — a colour, a size, a font stack. Angle brackets and quotes have no place in one.`,
        );
      } else if (URL_ATTRS.has(attr) && !isAcceptableUrl(raw)) {
        at(`${key} ${attr}: a URL in a theme must be https:.`);
      }
    }
  }
  return problems;
}

/** No angle brackets, no quotes — an attribute value that cannot leave its attribute. */
const ATTR_VALUE = /^[^<>"']*$/;

function escapeAttr(value: string): string {
  return value.replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch]!);
}

function escapeText(value: string): string {
  return value.replace(/[&<>]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[ch]!);
}

/**
 * The document both the editor preview and assembly compile (M3): one head
 * from the theme, one body from the blocks. Sorted keys and fixed
 * indentation, so the same theme, blocks and preheader always produce the
 * same bytes — the assembly cache key relies on it, and so does re-running
 * an assembly to reproduce a sent email.
 *
 * The preheader is escaped as text (P8): it is prose. Merge tokens survive
 * escaping untouched, since `{`, `}` and `|` are not special, and survive
 * compilation verbatim, so they are merged at send like everything else.
 */
export function hostDocument(
  theme: ThemeTokens,
  blocks: readonly string[],
  preheader?: string | null,
): string {
  const head: string[] = [];

  const fonts = Array.isArray(theme['mj-font']) ? (theme['mj-font'] as { name: string; href: string }[]) : [];
  for (const font of [...fonts].sort((a, b) => a.name.localeCompare(b.name))) {
    head.push(`    <mj-font name="${escapeAttr(font.name)}" href="${escapeAttr(font.href)}" />`);
  }

  const targets = Object.keys(theme)
    .filter((key) => (THEME_TARGETS as readonly string[]).includes(key))
    .sort();
  if (targets.length > 0) {
    head.push('    <mj-attributes>');
    for (const target of targets) {
      const attrs = theme[target] as Record<string, string>;
      const rendered = Object.keys(attrs)
        .sort()
        .map((attr) => ` ${attr}="${escapeAttr(attrs[attr]!)}"`)
        .join('');
      head.push(`      <${target}${rendered} />`);
    }
    head.push('    </mj-attributes>');
  }

  if (typeof theme['mj-breakpoint'] === 'string') {
    head.push(`    <mj-breakpoint width="${escapeAttr(theme['mj-breakpoint'])}" />`);
  }

  if (preheader && preheader.trim() !== '') {
    head.push(`    <mj-preview>${escapeText(preheader.trim())}</mj-preview>`);
  }

  return [
    '<mjml>',
    '  <mj-head>',
    ...head,
    '  </mj-head>',
    '  <mj-body>',
    ...blocks.map((block) => block.trim()),
    '  </mj-body>',
    '</mjml>',
    '',
  ].join('\n');
}
